import {
    AfterViewInit,
    Component,
    ContentChild,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    TemplateRef
} from '@angular/core';
import {UntypedFormGroup} from '@angular/forms';
import {BehaviorSubject, Subscription} from 'rxjs';
import {SelectFilterElement} from '../../../../../../../components/common/select-filter/select-filter.model';
import {Operator, Order, Sort} from '../../../../../../../shared/utils/url.builder';
import {LovValueModalOptions} from '../../model/lovValueModalOptions';
import {NewItemModalOptions} from '../../../../../../core/component/select/abstract-select.component';
import {
    LovValueSaveDialogInput,
    LovValueSaveDialogResult,
    LovValueUpdateModalComponent
} from '../lov-value-update-modal/lov-value-update-modal.component';
import {TriggerModalCapable} from '../../../../../../../components/common/modal/triggerModalCapable';
import {ContentDirective} from '../../../../../../../shared/directives/content.directive';
import {LovValueSummary} from '../../model/lovValueSummary.model';
import {LovValueSummaryFindAllService} from '../../../impl/service/lovValueSummaryFindAll.service';
import {UrlParameterBuilder} from '../../../../../../core/http/UrlParameterBuilder';

@Component({
    selector: 'app-lov-value-select-filter',
    templateUrl: './lov-value-select-filter.component.html',
    providers: [LovValueSummaryFindAllService]
})
export class LovValueSelectFilterComponent implements AfterViewInit, OnDestroy,
    TriggerModalCapable<LovValueModalOptions>,
    OnChanges {

    @ContentChild(ContentDirective, {static: true, read: TemplateRef}) template: TemplateRef<any>;

    @Input() deletable = false;
    @Input() disabled = false;
    @Input() parentFormGroup: UntypedFormGroup;
    @Input() parentFormControlName: string;
    @Input() lovListName: string;
    @Input() hideChildren = false;
    @Input() lovValues: LovValueSummary[] = [];
    @Input() lovValueValues: string[] = [];
    @Input() lovValuesToIgnore: LovValueSummary[] = [];
    @Input() lovValueValuesToIgnore: string[] = [];
    @Input() elementsPerPage: 10;
    @Input() sort = new Sort('label', Order.ASCENDING);
    @Input() excludeParent = false;
    @Input() parentValue: string;
    @Input() newItemModalOptions: LovValueModalOptions;
    @Input() filterLovValueProcessor: (criteria: string, element: LovValueSummary) => boolean;

    @Output() lovValuesElementChangeEvent = new EventEmitter<LovValueSummary[]>();
    @Output() selectionChangeEvent = new EventEmitter<LovValueSummary>();

    loadingElements = true;
    lovValueElements: BehaviorSubject<SelectFilterElement<string, LovValueSummary>[]> =
        new BehaviorSubject<SelectFilterElement<string, LovValueSummary>[]>([]);

    subscriptions: Subscription[] = [];

    constructor(public service: LovValueSummaryFindAllService) {

    }
    ngOnChanges(changes: SimpleChanges) {
        if (changes['lovListName'] || changes['parentValue'] || changes['lovValues']) {
            if (!this.lovValues || this.lovValues?.length === 0) {
                const urlBuilder = new UrlParameterBuilder()
                    .addParameter('list.name', this.lovListName, Operator.EQUALS)
                    .addInParameter('value', this.lovValueValues)
                    .addParameter('parent.value', this.parentValue, Operator.EQUALS);
                if (this.excludeParent === true) {
                    urlBuilder.addParameter('parent.id', 'null', Operator.NOT);
                }

                this.service.parameters = urlBuilder.parameters;
                this.service.sort = this.sort;
                this.refresh();
            } else {
                this.lovValueElements.next(this.lovValues.map(lovValue => {
                    return new SelectFilterElement(lovValue.value, lovValue.value, lovValue);
                }));
            }
        }
    }

    refresh() {
        this.service.findAll();
    }

    ngAfterViewInit(): void {
        this.subscriptions.push(this.service.loading$.subscribe(value => this.loadingElements = value));
        this.subscriptions.push(this.service.entities$.subscribe(entities => {

            if (!this.lovValues || this.lovValues?.length === 0) {
                // Remove children if requested
                let filteredResults = entities.filter((lovValue) => !this.hideChildren || (this.hideChildren && !lovValue.parentLabel));

                // Ignore provided lov values
                const lovValueToIgnore: string [] = this.lovValuesToIgnore.map(value => value.value);
                this.lovValueValuesToIgnore.forEach(value => lovValueToIgnore.push(value));
                filteredResults = filteredResults.filter(lovValue => lovValueToIgnore.indexOf(lovValue.value) === -1);

                this.lovValuesElementChangeEvent?.emit(filteredResults.map(value => value));
                this.lovValueElements.next(filteredResults.map(lovValue => {
                    return new SelectFilterElement(lovValue.value, lovValue.value, lovValue);
                }));
            }
        }));
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(value => value.unsubscribe());
    }

    get formattedNewItemModalOptions():
        NewItemModalOptions<LovValueSaveDialogResult, LovValueSaveDialogInput, LovValueUpdateModalComponent> {
        return this.newItemModalOptions ? ( {
            valueNewItemMapper: this.valueNewItemMapper,
            componentType: LovValueUpdateModalComponent,
            dialogOption: {
                title: 'Créer ' + this.newItemModalOptions.title,
            },
            dialogInput: {
                lovListName: this.lovListName
            }
        }) : undefined;
    }

    filterLovValues = (criteria: string, lovValue: LovValueSummary) => {
        if (this.filterLovValueProcessor) {
            return this.filterLovValueProcessor(criteria, lovValue);
        } else {
            return lovValue.label.toUpperCase().includes(criteria.toUpperCase());
        }
    }

    valueNewItemMapper = (result: LovValueSaveDialogResult) => {
        return result?.lovValue?.summary?.value;
    }

    selectionChanged(event: SelectFilterElement<string, LovValueSummary>){
        this.selectionChangeEvent?.emit(event?.element);
    }

}
